import {inject} from '@angular/core';
import {ResolveFn, Routes, UrlMatcher, UrlMatchResult, UrlSegment} from '@angular/router';
import {LoggedInComponent} from './components/logged-in/logged-in.component';
import {OrganizationSwitcherComponent} from './components/organization-select/organization-switcher.component';
import {OrganizationSelectedComponent} from './components/organization-selected/organization-selected.component';
import {HomeComponent} from './components/s3-browse/home-component/home.component';
import {FileDetailsComponent} from './components/s3-browse/file-details/file-details.component';
import {FolderComponent} from './components/s3-browse/folder/folder.component';
import {SearchPageComponent} from './components/search/search-page.component';
import {AudioOrderComponent} from './components/order-music/audio-order.component';
import {PreviewsComponent} from './components/previews/previews.component';
import {ListPreviewsForAirlineComponent} from './components/previews/list-previews-for-airline/list-previews.component';
import {PreviewPoitemComponent} from './components/previews/display-poitem-preview/preview-poitem.component';
import {WorkflowsComponent} from './components/workflows/workflows.component';
import {ListPosComponent} from './components/workflows/list-pos.component';
import {ListPoItems2Component} from './components/workflows/list-po-items2.component';
import {PoItemDetailComponent} from './components/workflows/po-item-details/po-item-details.component';
import {DisplayDeliveryReportComponent} from './reports/display-delivery-report/display-delivery-report.component';
import {RedirectLegacyComponent} from './redirect-legacy.component';
import {WelcomeComponent} from './components/welcome/welcome.component';
import {MfaGuard} from './mfa.guard';
import {CrdManagementComponent} from './components/crd-management/crd-management.component';
import {CrdSessionComponent} from './components/crd-session/crd-session.component';
import {crdJoinRedirect, crdSessionGuard} from './components/crd-session/crd-session-links';
import {VtkJobDetailComponent} from './components/workflows/vtk-jobs/vtk-job-detail.component';
import {VtkTaskLogComponent} from './components/workflows/vtk-jobs/vtk-task-log.component';
import {VtkJobOrgService} from './components/workflows/vtk-jobs/vtk-job-org.service';

/** Starts a job page's organization lookup with the navigation; resolves at once (VTK-3586). */
const prefetchJobOrg: ResolveFn<boolean> = route => inject(VtkJobOrgService).prefetch(route);
import {WhatsNewComponent} from './components/whats-new/whats-new.component';
import {MonitoringComponent} from './components/monitoring/monitoring.component';
import {PortalsComponent} from './components/content-acquisition/portals.component';
import {PortalDetailComponent} from './components/content-acquisition/portal-detail.component';
import {AsperaEndpointsComponent} from './components/aspera/aspera-endpoints.component';
import {AsperaEndpointDetailComponent} from './components/aspera/aspera-endpoint-detail.component';

export const customMatcher: UrlMatcher = (
  segments: UrlSegment[]
): UrlMatchResult => {
  const idPaths = segments.map(segment => segment.path);

  const mergedId = idPaths.join('/');
  const idSegment: UrlSegment = new UrlSegment(mergedId, {assetpath: mergedId});
  return (
    {
      consumed: segments,
      posParams: {assetpath: idSegment}
    });
};

export function prefixWithEncodedSlashMatcher(segments: UrlSegment[]): UrlMatchResult | null {

  if (segments.length === 2 && segments[1].path.endsWith('/')) {
    return {
      consumed: segments,
      posParams: {
        airline: segments[0],
        poprefix: segments[1],
      },
    };
  }
  return null;
}

export function poWithoutEncodedSlashMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length === 2 && !segments[1].path.endsWith('/')) {
    return {
      consumed: segments,
      posParams: {
        airline: segments[0],
        po: segments[1],
      },
    };
  }
  return null;
}

export const routes: Routes = [
  {
    path: 'reports/:organization_id/:po_name',
    component: DisplayDeliveryReportComponent
  },
  {
    path: 'reports/:organization_id/:po_name/:po_item_id',
    component: DisplayDeliveryReportComponent
  },
  {
    path: '',
    component: LoggedInComponent,
    children: [
      // One popover outlet for everything that opens *over* the page: the organization switcher,
      // the changelog and platform monitoring. Opening any of them keeps the primary URL intact, so
      // dismissing reveals the page the user deep-linked to, and only one can be up at a time --
      // which is the point of a single outlet rather than one per feature.
      {
        path: 'organizations',
        component: OrganizationSwitcherComponent,
        outlet: 'overlay'
      },
      {
        path: 'whats-new',
        component: WhatsNewComponent,
        outlet: 'overlay'
      },
      {
        path: 'monitoring',
        component: MonitoringComponent,
        outlet: 'overlay'
      },
      {path: '', pathMatch: 'full', component: WelcomeComponent},
      {
        path: 'o/castlabs',
        children: [
          {
            path: '**',
            component: RedirectLegacyComponent,
          },
          {
            path: '',
            component: RedirectLegacyComponent,
          },
        ],
      },
      {
        path: 'o/above_above',
        children: [
          {
            path: '**',
            component: RedirectLegacyComponent,
          },
          {
            path: '',
            component: RedirectLegacyComponent,
          },
        ],
      },
      {
        path: 'o/above_touch',
        children: [
          {
            path: '**',
            component: RedirectLegacyComponent,
          },
          {
            path: '',
            component: RedirectLegacyComponent,
          },
        ],
      },
      {
        path: 'o/:organization_id',
        component: OrganizationSelectedComponent,
        canActivate: [MfaGuard],
        children: [
          {path: '', pathMatch: 'full', redirectTo: 'files'},
          {
            path: 'signiant-portals',
            children: [
              {path: '', pathMatch: 'full', component: PortalsComponent},
              {path: ':portal_id', pathMatch: 'full', component: PortalDetailComponent},
            ]
          },
          {
            path: 'aspera-endpoints',
            children: [
              {path: '', pathMatch: 'full', component: AsperaEndpointsComponent},
              {path: ':endpoint_id', pathMatch: 'full', component: AsperaEndpointDetailComponent},
            ]
          },
          {
            path: 'files',
            children: [
              {
                path: '', pathMatch: 'full', component: HomeComponent,
              },
              {
                path: 'details', children: [{
                  matcher: customMatcher,
                  children: [
                    {path: '', pathMatch: 'full', component: FileDetailsComponent},
                  ]
                },
                ]
              },
              {
                path: 'browse', children: [{
                  matcher: customMatcher,
                  children: [
                    {path: '', pathMatch: 'full', component: FolderComponent},
                  ]
                },
                ]
              },

            ]
          },
          {path: 'search', pathMatch: 'full', component: SearchPageComponent},
          {path: 'audioorder', pathMatch: 'full', component: AudioOrderComponent},
          {path: 'edit-sessions', pathMatch: 'full', redirectTo: 'crd'},
          {path: 'crd', pathMatch: 'full', component: CrdManagementComponent},
          // Old per-screen links: the session page now joins each screen itself.
          {path: 'crd/join/:pool/:hostId', canActivate: [crdJoinRedirect], children: []},
          {path: 'crd/view/:pool/:hostId', canActivate: [crdSessionGuard], component: CrdSessionComponent},
          {
            path: 'previews', children: [
              {path: '', pathMatch: 'full', component: PreviewsComponent},
              {path: ':airline', pathMatch: 'full', redirectTo: ':airline/'},
              {path: ':airline/:po', pathMatch: 'full', component: ListPreviewsForAirlineComponent},
              {
                path: ':airline/:po/:poitem/preview',
                pathMatch: 'full',
                component: PreviewPoitemComponent,
              },
            ]
          },
          {
            path: 'workflows',
            children: [
              {path: '', pathMatch: 'full', component: WorkflowsComponent},
              {path: ':airline', pathMatch: 'full', component: ListPosComponent},
              {
                matcher: prefixWithEncodedSlashMatcher,
                pathMatch: 'full', component: ListPosComponent
              },
              {
                matcher: poWithoutEncodedSlashMatcher,
                pathMatch: 'full',
                component: ListPoItems2Component
              },
              {
                path: ':airline/:po/:poitem/vtk-jobs/:job_id/tasks/:task_id',
                pathMatch: 'full',
                resolve: {jobOrg: prefetchJobOrg},
                component: VtkTaskLogComponent
              },
              {
                path: ':airline/:po/:poitem/vtk-jobs/:job_id',
                pathMatch: 'full',
                resolve: {jobOrg: prefetchJobOrg},
                component: VtkJobDetailComponent
              },
              {
                path: ':airline/:po/:poitem/preview',
                pathMatch: 'full',
                component: PreviewPoitemComponent
              },
              {
                path: ':airline/:po/:poitem',
                pathMatch: 'full',
                component: PoItemDetailComponent
              },
            ]
          },
        ]
      },
    ],
  },
  {path: '**', redirectTo: '/'},

];
